import * as datefns from 'date-fns'
import ApiBase from './Api'
import { TradeRecord, TradesStat } from 'types/Instrument'
import { InstrumentEntity } from 'types/InstrumentEntity'
import { InstrumentPatchBody } from '@finance-lab/utils/types'

const INSTRUMENT_API_PATHS = {
    INSTRUMENT: (id: string) => `/instruments/${id}`,
    TRADES: (id: string) => `/instruments/${id}/trades`,
}

export type TradesType = {
    right: Record<string, TradeRecord<'right'>[]>
    unit: Record<string, TradeRecord<'unit'>[]>
    share: TradeRecord<'share'>[]
}

export class InstrumentApi extends ApiBase {
    async *getTrades(id: string) {
        const result: TradesType = {
            right: {},
            unit: {},
            share: [],
        }
        const startTime = performance.now()
        let isDone
        let page = 1

        while (!isDone) {
            const trades: TradesType = await this._getRequest(INSTRUMENT_API_PATHS.TRADES(id) + `?page=${page}`)

            if (!trades.share.length) {
                isDone = false
                break
            }

            const isFirstRun = result.share.length === 0

            result.share.unshift(...trades.share)

            for (const type of ['right', 'unit'] as Array<keyof Omit<typeof trades, 'share'>>) {
                for (const key in trades[type]) {
                    if (!result[type][key]) {
                        result[type][key] = []
                    }

                    result[type][key].unshift(...trades[type][key])
                }
            }

            if (isFirstRun) {
                console.debug('Fetched trades in', performance.now() - startTime)
            }

            yield { ...result }

            page++
        }

        return result
    }

    async getTradesStatsByEndingDate(instrumentId: string, endDate: Date, numberOfDays: number) {
        if (!instrumentId) {
            return null
        }
        const baseUrl = INSTRUMENT_API_PATHS.INSTRUMENT(instrumentId)
        const params = new URLSearchParams({
            end_date: datefns.format(endDate, 'yyyy-MM-dd'),
            number_of_days: numberOfDays.toString(),
        })

        const response = await this._getRequest(`${baseUrl}?${params.toString()}`)

        return response as TradesStat | null
    }

    async getDelistedInstruments() {
        const response: InstrumentEntity[] = await this._getRequest('/instruments?listing=delisted')

        return response
    }

    async update(instrumentToUpdate: { id: string } & InstrumentPatchBody): Promise<InstrumentEntity | null> {
        if (!instrumentToUpdate.id) {
            return null
        }

        const response = (await this._patchRequest(
            INSTRUMENT_API_PATHS.INSTRUMENT(instrumentToUpdate.id),
            instrumentToUpdate,
        )) as InstrumentEntity

        return response
    }
}
